import { useQuery } from "@tanstack/react-query";
import { useReadContracts } from "wagmi";
import { type Address } from "viem";
import { ERC721DropAbi } from "@atelier/abis";
import { ipfsToHttp } from "@/lib/ipfs";

export type Metadata = {
  name?: string;
  description?: string;
  image?: string;
  external_url?: string;
  attributes?: Array<{ trait_type?: string; value?: string | number }>;
};

/**
 * Bundles the four reads + the metadata JSON fetch a token-detail page
 * needs: tokenURI, ownerOf, collection name, revealed flag, and the
 * resolved metadata. Any read failing returns null for that field.
 *
 * `tokenIdMissing` is exposed so the page can render a "doesn't exist
 * yet" state when ownerOf reverts.
 */
export type UseTokenMetadataResult = {
  tokenUri: string | null;
  httpUri: string | null;
  owner: string | null;
  collectionName: string | null;
  revealed: boolean;
  tokenIdMissing: boolean;
  metadata: Metadata | null | undefined;
  metaLoading: boolean;
  metaError: boolean;
  refetch: () => unknown;
};

export function useTokenMetadata({
  address,
  chainId,
  tokenId,
  ipfsGateway,
  enabled,
}: {
  address: Address;
  chainId: number;
  tokenId: number;
  ipfsGateway: string;
  enabled: boolean;
}): UseTokenMetadataResult {
  const reads = useReadContracts({
    allowFailure: true,
    contracts: enabled
      ? ([
          { address, abi: ERC721DropAbi, functionName: "tokenURI", args: [BigInt(tokenId)], chainId },
          { address, abi: ERC721DropAbi, functionName: "ownerOf", args: [BigInt(tokenId)], chainId },
          { address, abi: ERC721DropAbi, functionName: "name", chainId },
          { address, abi: ERC721DropAbi, functionName: "revealed", chainId },
        ] as const)
      : [],
    query: { enabled, refetchInterval: 30_000 },
  });

  const tokenUri = reads.data?.[0]?.status === "success" ? (reads.data[0].result as string) : null;
  const owner = reads.data?.[1]?.status === "success"
    ? (reads.data[1].result as string).toLowerCase()
    : null;
  const collectionName = reads.data?.[2]?.status === "success"
    ? (reads.data[2].result as string)
    : null;
  const revealed = reads.data?.[3]?.status === "success"
    ? (reads.data[3].result as boolean)
    : true;
  const tokenIdMissing = reads.data?.[1]?.status === "failure";

  const httpUri = tokenUri ? ipfsToHttp(tokenUri, ipfsGateway) : null;

  // queryKey must match exactly what MintedGallery uses, so navigating from
  // the gallery into here is a cache hit. retry: 1 — placeholder/broken
  // baseURI is permanent failure, don't spin the loader forever.
  const { data: metadata, isLoading: metaLoading, isError: metaError } = useQuery({
    queryKey: ["tokenMeta", chainId, address, String(tokenId), httpUri],
    queryFn: async (): Promise<Metadata | null> => {
      if (!httpUri) return null;
      const res = await fetch(httpUri);
      if (!res.ok) throw new Error(`metadata ${res.status}`);
      return (await res.json()) as Metadata;
    },
    enabled: !!httpUri,
    staleTime: 60_000,
    retry: 1,
  });

  return {
    tokenUri,
    httpUri,
    owner,
    collectionName,
    revealed,
    tokenIdMissing,
    metadata,
    metaLoading,
    metaError,
    refetch: reads.refetch,
  };
}
